import React, {
  useContext, useState, useEffect, useMemo
} from 'react';
import PropTypes from 'prop-types';
import {
  params, string, shape, number, client, extend, useDataModel, arrayOf, QueryProvider
} from '@thd-nucleus/data-sources';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { DrawerHeader } from '@one-thd/sui-atomic-components';
import { AddToContext } from '../../AddToContext';
import {
  useListDetails, useUpdateList, listDetailsModel, updateListModel
} from '../../hooks/useLists';
import { useStorefrontData } from '../../hooks/useStorefrontData';
import { SCREENS, OPERATION_ADD_ITEMS, TRACK } from '../../AddToConstants';
import { triggerAnalytics } from '../../AddToHelpers';
import { SignIn } from '../../common/addto-signin/SignIn';
import { CreateList } from './CreateList';
import { SelectList } from './SelectList';
import { SelectListGroup } from './SelectListGroup';
import { AddToListSuccess } from './AddToListSuccess';
import { AddToLoading } from '../../common/addto-loading/AddToloading';
import { DefaultScreen } from '../../common/DefaultScreen';
import { MiniPip } from '../../common/mini-pip/MiniPip';
import { CopyList } from '../copy-list/CopyList';
import { IntentsPreview } from '../../common/intents-preview/IntentsPreview';
import { StarterLists } from './StarterLists';

// Screen title mapping
const SCREEN_TITLES = {
  [SCREENS.LIST_CREATE_LIST]: 'Create List',
  [SCREENS.LIST_SELECT_LIST]: 'Select List',
  [SCREENS.LIST_SELECT_GROUP]: 'Select Group',
  [SCREENS.DEFAULT]: 'Add Materials',
  [SCREENS.MINI_PIP_PRODUCT_REVIEWS]: 'Add Materials',
  [SCREENS.MINI_PIP_PRODUCT_SPECS]: 'Add Materials',
  [SCREENS.MINI_PIP_PRODUCT_OVERVIEW]: 'Add Materials',
  [SCREENS.MINI_PIP_BASE]: 'Add Materials',
  [SCREENS.COPY_LIST_SUMMARY]: 'Copy from Your Lists',
  [SCREENS.COPY_LIST_PRODUCTS]: 'Copy from Your Lists',
  [SCREENS.GENERATED_LIST_INTENTS_PREVIEW]: 'Preview List',
  [SCREENS.STARTER_LISTS]: 'Starter Lists',
};

export const AddToList = (props) => {
  const {
    products, skipListId, isSharedList, listId, onMaterialsAdded,
    productPodItemData, aiListBuilderData
  } = props;

  const {
    title: listName = '', materials = {}, onNewIntentsSave, refId = '', refType = 'LIST'
  } = aiListBuilderData || {};

  const theme = useTheme(AddToList, props);
  const { disableSignInDrawer } = theme.props;

  const {
    addToType,
    storeId,
    setScreen,
    screen,
    handleClose,
    selectedProductDetails,
    setShowSuccessMessage,
    showSuccessMessage,
    setShowErrorMessage,
    setSelectedListId,
    setSelectedListName,
    atcDrawerType,
    setAtcDrawerType,
    searchKeyword,
    previousScreen,
    sourceScreen
  } = useContext(AddToContext);
  const { svocID, userID, customerType, isLoggedIn } = useThdCustomer() || {};
  const [isSearchResults, setIsSearchResults] = useState(Boolean(productPodItemData?.searchKeyword));
  const [favoriteCount, setFavoriteCount] = useState(0);
  const [newListDetails, setNewListDetails] = useState({
    listName: '', listId: ''
  });
  const [selectedList, setSelectedList] = useState({
    listId: null,
    listName: null
  });
  const [keyword, setKeyword] = useState(productPodItemData?.searchKeyword || '');
  const {
    LIST_CREATE_LIST,
    LIST_SELECT_LIST,
    LIST_SELECT_GROUP,
    SIGN_IN,
    LIST_SUCCESS,
    LOADING,
    DEFAULT,
    MINI_PIP_BASE,
    MINI_PIP_PRODUCT_OVERVIEW,
    MINI_PIP_PRODUCT_SPECS,
    MINI_PIP_PRODUCT_REVIEWS,
    GENERATED_LIST_INTENTS_PREVIEW,
    MINI_PIP_FULL,
    STARTER_LISTS
  } = SCREENS;
  const { storeFrontSVOCID, storeFrontUserID } = useStorefrontData();
  const itemId = selectedProductDetails?.itemId || products[0]?.itemId;

  const { data } = useDataModel('product', {
    variables: {
      itemId,
      storeId
    },
    skip: !itemId || products?.length > 1
  });

  const productData = data?.product || {};
  const productValue = productData?.pricing?.value;
  const identifiers = productData?.identifiers;
  const primaryImage = productData?.media?.images?.find(
    (img) => img.type === 'IMAGE' && img.subType === 'PRIMARY'
  );

  const selectedBundleItems = selectedProductDetails?.productData?.product?.bundleItems
    || selectedProductDetails?.productData?.product?.bundleSpecificationDetails?.components;
  const isItemBundle = productData?.bundleFlag || selectedBundleItems?.length > 0;
  const bundleItems = productData?.bundleItems || selectedBundleItems || [];
  const bundledProducts = bundleItems.map((item) => ({
    itemId: item.id || item.defaultProductId,
    quantity: item.quantity || selectedProductDetails?.quantity || 1
  }));

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('AddToListDrawer.ready');
  }, []);

  const canSkipListDetails = useMemo(() => {
    if (products?.length === 0) {
      return true;
    }
    if (storeFrontSVOCID) {
      return (!storeFrontSVOCID || !storeFrontUserID) || addToType !== 'List';
    }
    return (!svocID || !userID) || (addToType !== 'List' && atcDrawerType !== 'List');

  }, [products?.length, storeFrontSVOCID, storeFrontUserID, svocID, userID, addToType]);

  const { listData, listLoading, listRefetch } = useListDetails({
    customerAccountID: storeFrontSVOCID || svocID,
    userId: storeFrontSVOCID ? storeFrontUserID : userID,
    customerType: storeFrontSVOCID ? null : customerType?.toUpperCase(),
    skip: canSkipListDetails
  });

  const { listDetails: listDetailsData = null } = listData?.listDetails?.listResponse || {};
  const listDetails = (listDetailsData || []).filter((list) => list.listId !== skipListId);
  const listsExist = !!listDetails?.length;

  const onCreateList = () => {
    listRefetch();
  };

  const { updateList, updateListResponse } = useUpdateList({
    id: listId,
    operationName: OPERATION_ADD_ITEMS
  });

  const favoriteDetailCount = productData?.favoriteDetail?.count;

  useEffect(() => {
    setFavoriteCount(favoriteDetailCount);
  }, [favoriteDetailCount]);

  const shouldShowSelectList = useMemo(() => {
    return listsExist;
  }, [listLoading]);

  useEffect(() => {
    // Don't change screens when showing success message
    if (showSuccessMessage) {
      return;
    }
    if (!disableSignInDrawer && !isLoggedIn) {
      setScreen(SIGN_IN);
    } else if (listLoading) {
      setScreen(LOADING);
    } else if (products.length === 0) {
      setScreen(DEFAULT);
    } else if (shouldShowSelectList) {
      setScreen(LIST_SELECT_LIST);
    } else if (screen !== LIST_SUCCESS) {
      setScreen(LIST_CREATE_LIST);
    }
  }, [isLoggedIn, listsExist, listLoading]);

  useEffect(() => {
    if (updateListResponse?.error) {
      setShowErrorMessage(true);
    }
    if (updateListResponse?.data?.updateList?.id) {
      // Set the list information in context for the success message
      const updatedListId = updateListResponse?.data?.updateList?.id;
      const selectedListName = selectedList?.listName || newListDetails?.listName;

      if (updatedListId && selectedListName) {
        setSelectedListId(updatedListId);
        setSelectedListName(selectedListName);
      }

      if (onMaterialsAdded) {
        onMaterialsAdded();
      }
      setShowSuccessMessage(true);
    }
  }, [updateListResponse.error, updateListResponse.data, selectedList?.listName, newListDetails?.listName]);

  const onSearch = (updatedKeyword) => {
    setKeyword(updatedKeyword);
  };

  // Handle back from mini-PLP (when on DEFAULT screen with search results visible)
  const handleBackPress = () => {
    triggerAnalytics(TRACK.ADD_TO_OVERLAY_EXIT, { screen });
    if (screen === DEFAULT && isSearchResults) {
      setIsSearchResults(false);
      return;
    }
    if (screen === LIST_CREATE_LIST && !!listsExist) {
      setScreen(LIST_SELECT_LIST);
      return;
    }
    if (addToType === 'Cart' && !!atcDrawerType) {
      setScreen(MINI_PIP_FULL);
      setAtcDrawerType('');
      return;
    }
    if (screen === LIST_SELECT_GROUP) {
      setScreen(LIST_SELECT_LIST);
      return;
    }
    if (screen === MINI_PIP_BASE) {
      setScreen(DEFAULT);
      return;
    }
    // eslint-disable-next-line max-len
    if (screen === MINI_PIP_PRODUCT_OVERVIEW || screen === MINI_PIP_PRODUCT_SPECS || screen === MINI_PIP_PRODUCT_REVIEWS) {
      setScreen(MINI_PIP_BASE);
      return;
    }
    if (screen === GENERATED_LIST_INTENTS_PREVIEW) {
      setScreen(previousScreen || DEFAULT);
      return;
    }
    if (screen === STARTER_LISTS) {
      setScreen(DEFAULT);
      setIsSearchResults(false);
      return;
    }
    if (screen?.startsWith('COPY_LIST')) {
      setScreen(DEFAULT);
      setIsSearchResults(false);
      return;
    }
    if (screen === LIST_SUCCESS) {
      handleClose();

    }
  };

  const showBackButton = (
    !(
      (screen === LIST_SELECT_LIST && products?.length && addToType !== 'Cart')
        || (screen === DEFAULT)
        || (screen === LIST_CREATE_LIST && !listsExist)
    )
  ) || isSearchResults;

  const onAddItemToIntent = ({ variables }) => {
    const { onIntentResolved } = productPodItemData || {};
    const { products: resolvedProduct } = variables;
    const [item] = resolvedProduct;
    if (onIntentResolved) {
      onIntentResolved(item, searchKeyword);
      handleClose();
    }
  };

  const listIdToPass = selectedList?.listId || newListDetails?.listId;
  const headerTitleText = SCREEN_TITLES[screen] || 'Add to List';

  return (
    <>
      {
        !screen?.startsWith('COPY_LIST') && (
          <DrawerHeader
            data-testid="add-to-drawer-header"
            title={headerTitleText}
            onClose={() => handleClose('close')}
            onBack={showBackButton ? handleBackPress : null}
          />
        )
      }
      {screen === SIGN_IN && (
        <SignIn
          onClose={handleClose}
        />
      )}
      {screen === LOADING && (
        <AddToLoading />
      )}
      {screen === DEFAULT && (
        <DefaultScreen
          keyword={keyword}
          onSearch={onSearch}
          products={products}
          handleClose={handleClose}
          updateList={productPodItemData?.onIntentResolved ? onAddItemToIntent : updateList}
          customerType={customerType}
          isSearchResults={isSearchResults}
          setIsSearchResults={setIsSearchResults}
          name={listName}
          materials={materials}
          productPodItemData={productPodItemData}
          aiListBuilderData={aiListBuilderData}
        />
      )}
      {screen === LIST_CREATE_LIST && (
        <CreateList
          onClose={handleClose}
          onCreate={onCreateList}
          products={products}
          isExistingLists={listsExist}
          isSharedList={isSharedList}
          setFavoriteCount={setFavoriteCount}
          favoriteCount={favoriteCount}
          productValue={productValue}
          identifiers={identifiers}
          primaryImage={primaryImage}
          setNewListDetails={setNewListDetails}
          isItemBundle={isItemBundle}
          bundledProducts={bundledProducts}
        />
      )}
      {screen === LIST_SELECT_LIST && (
        <SelectList
          products={products}
          listDetails={listDetails}
          onClose={handleClose}
          isSharedList={isSharedList}
          setFavoriteCount={setFavoriteCount}
          favoriteCount={favoriteCount}
          productValue={productValue}
          identifiers={identifiers}
          primaryImage={primaryImage}
          setNewListDetails={setNewListDetails}
          onCreate={onCreateList}
          isExistingLists={listsExist}
          selectedList={selectedList}
          setSelectedList={setSelectedList}
        />
      )}
      {screen === LIST_SELECT_GROUP && (
        <SelectListGroup
          listId={listIdToPass || listId}
          storeId={storeId}
          isSharedList={isSharedList}
          products={selectedProductDetails?.itemId ? [{
            itemId: selectedProductDetails?.itemId,
            quantity: selectedProductDetails?.quantity,
            price: selectedProductDetails?.productData?.product?.pricing?.value
          }] : products}
          identifiers={identifiers}
          primaryImage={primaryImage}
          handleClose={handleClose}
          isNewlyCreated={!!newListDetails?.listId && !selectedList?.listId}
          newListName={newListDetails?.listName}
          isItemBundle={isItemBundle}
          bundledProducts={bundledProducts}
        />
      )}
      {
        screen?.startsWith('COPY_LIST') && (
          <CopyList
            onClose={handleClose}
            listId={listId}
            onMaterialsAdded={onMaterialsAdded}
          />
        )
      }
      {screen === LIST_SUCCESS && (
        <AddToListSuccess
          newListDetails={newListDetails}
          onClose={handleClose}
          products={products}
        />
      )}
      {
        screen?.startsWith('MINI_PIP') && (
          <MiniPip
            itemId={itemId}
            isSearchResults={isSearchResults}
          />
        )
      }
      {
        screen === GENERATED_LIST_INTENTS_PREVIEW && (
          <QueryProvider cacheKey="intents-preview">
            <IntentsPreview
              onNewIntentsSave={onNewIntentsSave}
              refId={refId}
              refType={refType}
            />
          </QueryProvider>
        )
      }
      {
        screen === STARTER_LISTS && (
          <StarterLists aiListBuilderData={aiListBuilderData} />
        )
      }
    </>
  );
};

AddToList.displayName = 'AddToList';

AddToList.propTypes = {
  isSharedList: PropTypes.bool,
  listId: PropTypes.string,
  onMaterialsAdded: PropTypes.func,
  products: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number,
    price: PropTypes.number,
    intent: PropTypes.string
  })),
  skipListId: PropTypes.bool,
  productPodItemData: PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number,
    searchKeyword: PropTypes.string,
    onIntentResolved: PropTypes.func,
  }),
  aiListBuilderData: PropTypes.shape({
    onNewIntentsSave: PropTypes.func,
    title: PropTypes.string,
    refId: PropTypes.string,
    refType: PropTypes.string,
    materials: PropTypes.arrayOf(PropTypes.shape({
      groupName: PropTypes.string,
      products: PropTypes.arrayOf(PropTypes.shape({
        quantity: PropTypes.number,
        product: PropTypes.string,
      }))
    }))
  }),
};

AddToList.defaultProps = {
  isSharedList: false,
  listId: '',
  onMaterialsAdded: () => { },
  products: [],
  skipListId: false,
  productPodItemData: {
    onIntentResolved: () => {}
  },
  aiListBuilderData: {}
};

const favoriteDetailCount = shape({
  count: number()
});

if (favoriteDetailCount.skip) {
  favoriteDetailCount.skip('skipFavoriteCount', false);
}

AddToList.dataModel = extend(
  {
    product: params({ itemId: string().isRequired(), dataSource: string() }).shape({
      itemId: string(),
      dataSource: string(),
      dataSources: string(),
      favoriteDetail: client(favoriteDetailCount),
      pricing: params({ storeId: string() }).shape({
        value: number({ float: true })
      }),
      identifiers: shape({
        brandName: string(),
        productLabel: string()
      }),
      media: shape({
        images: arrayOf(shape({
          url: string(),
          type: string(),
          subType: string()
        }))
      })
    })
  },
  DefaultScreen,
  CreateList,
  SelectListGroup,
  listDetailsModel,
  updateListModel,
  CopyList,
  AddToListSuccess
);